import { If } from '@paytrace/pt-skel/Component'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import React, { PropsWithChildren, useCallback, useState } from 'react'
import { EDConfirmationDialog } from '../../../Component/EnhancedData/EDConfirmationDialog'
import EDInputText from '../../../Component/EnhancedData/EDInputText'
import { BackendErrorFeedback, ValidationErrorFeedback } from '../../../Component/EnhancedData/EDResponseFeedbacks'
import { DataWithKey, TextFieldDefinition } from '../../../Component/EnhancedData/Types'
import { ED_FIELD_SPEC } from '../../../Helper/EnhancedData/Constants'
import { hasBackendError, makePersistentKey } from '../../../Helper/EnhancedData/EnhancedDataUtils'
import { toMap } from '../../../Helper/util'
import { L2SummaryProps } from '../../../Type/EnhancedData'

const PAGE_TAG: string = 'LevelTwoSummary'

const fieldDefs: DataWithKey<TextFieldDefinition>[] = [
  {
    key: 'customerReferenceId',
    name: 'customerReferenceId',
    label: ED_FIELD_SPEC.level2.customerReferenceId.displayName,
    showRequired: true,
    tooltip: ED_FIELD_SPEC.level2.customerReferenceId.tooltip,
    maxLength: ED_FIELD_SPEC.level2.customerReferenceId.maxLength,
    minLength: ED_FIELD_SPEC.level2.customerReferenceId.minLength,
    pattern: ED_FIELD_SPEC.level2.customerReferenceId.pattern,
    errorMessage: ED_FIELD_SPEC.level2.customerReferenceId.errorMessage,
  },
  {
    key: 'sourceZipCode',
    name: 'sourceZipCode',
    label: ED_FIELD_SPEC.level2.sourceZipCode.displayName,
    showRequired: true,
    tooltip: ED_FIELD_SPEC.level2.sourceZipCode.tooltip,
    maxLength: ED_FIELD_SPEC.level2.sourceZipCode.maxLength,
    minLength: ED_FIELD_SPEC.level2.sourceZipCode.minLength,
    pattern: ED_FIELD_SPEC.level2.sourceZipCode.pattern,
    errorMessage: ED_FIELD_SPEC.level2.sourceZipCode.errorMessage,
  },
  {
    name: 'shippingZipCode',
    key: 'shippingZipCode',
    label: ED_FIELD_SPEC.level2.shippingZipCode.displayName,
    showRequired: true,
    tooltip: ED_FIELD_SPEC.level2.shippingZipCode.tooltip,
    maxLength: ED_FIELD_SPEC.level2.shippingZipCode.maxLength,
    minLength: ED_FIELD_SPEC.level2.shippingZipCode.minLength,
    pattern: ED_FIELD_SPEC.level2.shippingZipCode.pattern,
    errorMessage: ED_FIELD_SPEC.level2.shippingZipCode.errorMessage,
  },
  {
    key: 'shippingCountry',
    name: 'shippingCountry',
    label: ED_FIELD_SPEC.level2.shippingCountry.displayName,
    showRequired: true,
    tooltip: ED_FIELD_SPEC.level2.shippingCountry.tooltip,
    maxLength: ED_FIELD_SPEC.level2.shippingCountry.maxLength,
    minLength: ED_FIELD_SPEC.level2.shippingCountry.minLength,
    pattern: ED_FIELD_SPEC.level2.shippingCountry.pattern,
    errorMessage: ED_FIELD_SPEC.level2.shippingCountry.errorMessage,
  },
  {
    key: 'invoice',
    name: 'invoice',
    label: ED_FIELD_SPEC.level2.invoice.displayName,
    showRequired: true,
    tooltip: ED_FIELD_SPEC.level2.invoice.tooltip,
    maxLength: ED_FIELD_SPEC.level2.invoice.maxLength,
    minLength: ED_FIELD_SPEC.level2.invoice.minLength,
    pattern: ED_FIELD_SPEC.level2.invoice.pattern,
    errorMessage: ED_FIELD_SPEC.level2.invoice.errorMessage,
  },
  {
    key: 'merchantTaxId',
    name: 'merchantTaxId',
    label: ED_FIELD_SPEC.level2.merchantTaxId.displayName,
    showRequired: false,
    tooltip: ED_FIELD_SPEC.level2.merchantTaxId.tooltip,
    maxLength: ED_FIELD_SPEC.level2.merchantTaxId.maxLength,
    minLength: ED_FIELD_SPEC.level2.merchantTaxId.minLength,
    pattern: ED_FIELD_SPEC.level2.merchantTaxId.pattern,
    errorMessage: ED_FIELD_SPEC.level2.merchantTaxId.errorMessage,
  },
  {
    key: 'customerTaxId',
    name: 'customerTaxId',
    label: ED_FIELD_SPEC.level2.customerTaxId.displayName,
    showRequired: false,
    tooltip: ED_FIELD_SPEC.level2.customerTaxId.tooltip,
    maxLength: ED_FIELD_SPEC.level2.customerTaxId.maxLength,
    minLength: ED_FIELD_SPEC.level2.customerTaxId.minLength,
    pattern: ED_FIELD_SPEC.level2.customerTaxId.pattern,
    errorMessage: ED_FIELD_SPEC.level2.customerTaxId.errorMessage,
  },
]
const fieldDefMap: Record<string, DataWithKey<TextFieldDefinition>> = toMap(fieldDefs)

export function LevelTwoSummarySection ({ appContext, ...props }: L2SummaryProps): React.JSX.Element {
  const KEY_DONT_SHOW_ADD_LINE_ITEM_MODAL_AGAIN = makePersistentKey(
    'dontShowAddLineItemModalAgain', PAGE_TAG, appContext.merchant
  )

  // Props
  const { formData, isSaveButtonClicked, isPartialPage, selectedTaxOption, ...callbacks } = props
  const { onInputChange, onInputError, onTaxExemptChange, onAddLineItemNavClick, ...errorProps } = callbacks
  const { backendError, validationError, showValidationErrorBanner } = errorProps

  // States
  const [showModal, setShowModal] = useState<boolean>(false)
  const [dontShowModalAgain, setDontShowModalAgain] = useState(
    Persistence.getItem(KEY_DONT_SHOW_ADD_LINE_ITEM_MODAL_AGAIN) || false
  )

  const handleAddLineItemModelCancel = useCallback(() => {
    setShowModal(false)
  }, [])

  const handleAddLineItemModelConfirm = useCallback(() => {
    setShowModal(false)
    onAddLineItemNavClick(true) // updating parent component's state with callback
  }, [onAddLineItemNavClick])

  const handleAddLineItemLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (dontShowModalAgain) {
      handleAddLineItemModelConfirm() // usually a generic behaviour
    } else {
      setShowModal(true)
    }
  }, [dontShowModalAgain, handleAddLineItemModelConfirm])

  const handleDontShowModalAgainChange = useCallback((isChecked: boolean):void => {
    setDontShowModalAgain(isChecked)
    Persistence.setItem(KEY_DONT_SHOW_ADD_LINE_ITEM_MODAL_AGAIN, isChecked)
  }, [KEY_DONT_SHOW_ADD_LINE_ITEM_MODAL_AGAIN])

  // let prefixInput = '$'
  // let readOnly = false
  // let disabled = false
  // let className = 'enhanced-data-input'
  //
  // if (selectedTaxOption === 'line-item') {
  //   readOnly = true
  //   className = 'enhanced-data-input static-input'
  // } else if (selectedTaxOption === 'exempt') {
  //   prefixInput = ' '
  //   disabled = true
  // }

  return (
    <div className={'level-2-section'}>
      <h1 style={{ color: 'black' }}>Enhanced Data</h1>
      { hasBackendError(backendError) &&
          (
            <>
              <BackendErrorFeedback {...backendError}/>
              <div className="horizontal-break"/>
            </>
          )
      }
      <If is={showValidationErrorBanner}>
        <ValidationErrorFeedback {...validationError}/>
      </If>
      <LevelTwoSummaryField>
        <EDInputText
          value={formData.customerReferenceId || ''}
          fieldDefinition={fieldDefMap?.customerReferenceId}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
        <EDInputText
          value={formData?.customerTaxId}
          fieldDefinition={fieldDefMap.customerTaxId}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
        <EDInputText
          value={formData.invoice || ''}
          fieldDefinition={fieldDefMap.invoice}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
        <EDInputText
          value={formData.shippingZipCode || ''}
          fieldDefinition={fieldDefMap.shippingZipCode}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
        <EDInputText
          value={formData.shippingCountry || ''}
          fieldDefinition={fieldDefMap.shippingCountry}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
        <EDInputText
          value={formData.sourceZipCode || ''}
          fieldDefinition={fieldDefMap.sourceZipCode}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
        <EDInputText
          value={formData?.merchantTaxId || ''}
          fieldDefinition={fieldDefMap.merchantTaxId}
          className={'enhanced-data-input'}
          onInputChange={onInputChange}
          onSummaryInputError={onInputError}
          runValidationNow={isSaveButtonClicked}
        />
      </LevelTwoSummaryField>
      <div className='horizontal-break'/>
      <If is={isPartialPage}>
        <a href="" className="strong-anchor" onClick={handleAddLineItemLinkClick}>
          Add Product Line Item Data
        </a>
      </If>
      <EDConfirmationDialog
        isVisible={showModal}
        title='Additional line item data is optional'
        text='You may provide additional line item data for this transaction; however, based on this transaction’s card type, providing this information is unlikely to obtain better processing rates.'
        onCancel={handleAddLineItemModelCancel}
        onConfirm={handleAddLineItemModelConfirm}
        className='enhanced-data'
        cancelButtonLabel='Go Back'
        confirmButtonLabel='Add Product Line Item Data'
        onDoNotShowAgainChange={handleDontShowModalAgainChange}
      />
    </div>
  )
}

function LevelTwoSummaryField ({ children }: PropsWithChildren) {
  return <div className="level-2-summary">{children}</div>
}
